import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import {
  Apple,
  Calendar as CalendarIcon,
  Check,
  Copy,
  Eye,
  EyeOff,
  Mail,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { http, HttpError } from "@/lib/api";
import { useAuthStore } from "@/stores/useAuthStore";
import { useRadiologistsStore } from "@/features/radiologists/store/useRadiologistsStore";
import { useRadAvailabilityStore } from "@/features/radiologists/store/useRadAvailabilityStore";
import type { CalendarSubscriptionInfo } from "../types/calendar-feed";
import { detectPlatform } from "../lib/platform";
import { compareRadiologists } from "@/features/radiologists/utils/radName";

const SELF_FEED_KEY = ["calendar-feed", "me"] as const;
const SUBSCRIPTION_KEY = ["calendar-subscription", "me"] as const;

// /me returns a combined feed covering all rads the account is linked to
// (an account may own more than one rad), shaped like a subscription.
async function fetchSelfFeed(): Promise<CalendarSubscriptionInfo> {
  return http<CalendarSubscriptionInfo>("/api/v1/calendar/me");
}
async function regenerateSelfFeed(): Promise<CalendarSubscriptionInfo> {
  return http<CalendarSubscriptionInfo>("/api/v1/calendar/me/regenerate", { method: "POST" });
}
async function fetchSubscription(): Promise<CalendarSubscriptionInfo> {
  return http<CalendarSubscriptionInfo>("/api/v1/calendar/subscriptions/me");
}
async function patchSubscription(rad_ids: number[]): Promise<CalendarSubscriptionInfo> {
  return http<CalendarSubscriptionInfo>("/api/v1/calendar/subscriptions/me", {
    method: "PATCH",
    body: JSON.stringify({ rad_ids }),
    headers: { "Content-Type": "application/json" },
  });
}
async function regenerateSubscription(): Promise<CalendarSubscriptionInfo> {
  return http<CalendarSubscriptionInfo>("/api/v1/calendar/subscriptions/me/regenerate", {
    method: "POST",
  });
}

type CalendarSyncDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type Mode = "self" | "tracked";

function detectMode(roles: string[]): Mode {
  // Plain radiologist accounts get their own single-rad feed (the existing /me
  // endpoint). Anyone else (admins, schedulers, support) gets the multi-rad
  // "build your own" tracked-calendar UI.
  if (roles.length <= 1 && (roles.length === 0 || roles[0] === "radiologist")) {
    return "self";
  }
  return "tracked";
}

export function CalendarSyncDialog({ open, onOpenChange }: CalendarSyncDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sync to your calendar</DialogTitle>
          <DialogDescription>
            See shifts in Google Calendar, Apple Calendar, or Outlook. Updates automatically when
            the schedule changes.
          </DialogDescription>
        </DialogHeader>
        {/* Mount body only while open so the network call is lazy. */}
        {open ? <CalendarSyncBody /> : null}
      </DialogContent>
    </Dialog>
  );
}

function CalendarSyncBody() {
  const roles = useAuthStore((s) => s.roles);
  const mode = useMemo(() => detectMode(roles), [roles]);

  if (mode === "self") {
    return <SelfFeedView />;
  }
  return <TrackedFeedView />;
}

function UnlinkedEmptyState() {
  return (
    <div className="rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
      Your account isn&apos;t linked to a radiologist yet, so there&apos;s no schedule to sync. Ask an admin
      to link your account to a radiologist.
    </div>
  );
}

// --- Self mode (radiologist viewing their own) ----------------------------

function SelfFeedView() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: SELF_FEED_KEY,
    queryFn: fetchSelfFeed,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const regenerate = useMutation({
    mutationFn: regenerateSelfFeed,
    onSuccess: (next) => {
      queryClient.setQueryData(SELF_FEED_KEY, next);
      toast.success("Calendar URL regenerated. Old subscriptions will stop updating.");
    },
    onError: () => toast.error("Failed to regenerate calendar URL."),
  });

  // /me returns 404 when the logged-in account isn't linked to any rad.
  if (isError && error instanceof HttpError && error.status === 404) {
    return <UnlinkedEmptyState />;
  }

  if (isLoading) {
    return <FeedLoading />;
  }
  if (isError || !data) {
    return <FeedError message={error?.message} />;
  }

  return (
    <FeedBody
      urls={data}
      canRegenerate
      onRegenerate={() => regenerate.mutate()}
      regenerating={regenerate.isPending}
    />
  );
}

// --- Tracked mode (admins / schedulers / support / scribes) ---------------

type ShiftPresetKey = "all" | "daytime" | "mid" | "night";

type ShiftPreset = {
  key: ShiftPresetKey;
  label: string;
  /** Inclusive start halfhour (0..47) and exclusive end halfhour (1..48). */
  startHalfhour: number;
  endHalfhour: number;
};

const SHIFT_PRESETS: ShiftPreset[] = [
  { key: "all", label: "All", startHalfhour: 0, endHalfhour: 48 },
  { key: "daytime", label: "Daytime", startHalfhour: 16, endHalfhour: 34 }, // 8a-5p
  { key: "mid", label: "Mid", startHalfhour: 34, endHalfhour: 48 }, // 5p-12a
  { key: "night", label: "Night", startHalfhour: 0, endHalfhour: 16 }, // 12a-8a
];

const PRESET_WINDOW_DAYS = 30;

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function TrackedFeedView() {
  const queryClient = useQueryClient();
  const { radiologists, list } = useRadiologistsStore();
  const { availability, fetch: fetchAvailability } = useRadAvailabilityStore();

  useEffect(() => {
    if (Object.keys(radiologists).length === 0) {
      void list();
    }
  }, [radiologists, list]);

  const activeRads = useMemo(
    () =>
      Object.values(radiologists)
        .filter((r) => r.active)
        .sort(compareRadiologists),
    [radiologists],
  );

  // Lazily fetch availability for all active rads (last/next ~30 days) so the
  // preset chips can classify rads by their typical shift hours. Kicked off
  // once the rad list is loaded.
  useEffect(() => {
    if (activeRads.length === 0) return;
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - Math.floor(PRESET_WINDOW_DAYS / 2));
    const end = new Date(today);
    end.setDate(end.getDate() + Math.floor(PRESET_WINDOW_DAYS / 2));
    void fetchAvailability(
      activeRads.map((r) => r.id),
      isoDate(start),
      isoDate(end),
    );
  }, [activeRads, fetchAvailability]);

  const subscriptionQuery = useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: fetchSubscription,
    staleTime: 5 * 60_000,
  });

  const updateMutation = useMutation({
    mutationFn: patchSubscription,
    onMutate: async (radIds) => {
      await queryClient.cancelQueries({ queryKey: SUBSCRIPTION_KEY });
      const prev = queryClient.getQueryData<CalendarSubscriptionInfo>(SUBSCRIPTION_KEY);
      if (prev) {
        queryClient.setQueryData<CalendarSubscriptionInfo>(SUBSCRIPTION_KEY, {
          ...prev,
          rad_ids: radIds,
        });
      }
      return { prev };
    },
    onError: (_err, _radIds, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(SUBSCRIPTION_KEY, ctx.prev);
      toast.error("Couldn't save your calendar selection.");
    },
    onSuccess: (next) => {
      queryClient.setQueryData(SUBSCRIPTION_KEY, next);
    },
  });

  const regenerate = useMutation({
    mutationFn: regenerateSubscription,
    onSuccess: (next) => {
      queryClient.setQueryData(SUBSCRIPTION_KEY, next);
      toast.success("Calendar URL regenerated. Old subscriptions will stop updating.");
    },
    onError: () => toast.error("Failed to regenerate calendar URL."),
  });

  // Server is source of truth, but optimistic updates show the in-flight value
  // immediately so toggling chips feels instant.
  const selectedIds = useMemo(
    () => subscriptionQuery.data?.rad_ids ?? [],
    [subscriptionQuery.data?.rad_ids],
  );
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const radsByBucket = useMemo(() => {
    return classifyRadsByBucket(activeRads, availability);
  }, [activeRads, availability]);

  const applyPreset = (preset: ShiftPreset) => {
    const ids = preset.key === "all" ? activeRads.map((r) => r.id) : radsByBucket[preset.key];
    updateMutation.mutate(ids);
  };

  const clearAll = () => updateMutation.mutate([]);

  const toggleRad = (radId: number) => {
    const next = selectedSet.has(radId)
      ? selectedIds.filter((id) => id !== radId)
      : [...selectedIds, radId];
    updateMutation.mutate(next);
  };

  const presetIsActive = (preset: ShiftPreset) => {
    if (activeRads.length === 0) return false;
    const target =
      preset.key === "all" ? activeRads.map((r) => r.id) : (radsByBucket[preset.key] ?? []);
    if (target.length === 0) return false;
    return arraysEqual(
      [...selectedIds].sort((a, b) => a - b),
      [...target].sort((a, b) => a - b),
    );
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Radiologists to track</Label>
          <span className="text-[11px] text-muted-foreground">
            {selectedIds.length} of {activeRads.length} selected
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {SHIFT_PRESETS.map((p) => (
            <Button
              key={p.key}
              type="button"
              variant={presetIsActive(p) ? "default" : "outline"}
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => applyPreset(p)}
            >
              {p.label}
            </Button>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={clearAll}
            disabled={selectedIds.length === 0}
          >
            Clear
          </Button>
        </div>

        <div className="max-h-56 overflow-y-auto rounded-md border border-border bg-background/40">
          {activeRads.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">No active radiologists.</p>
          ) : (
            <ul className="divide-y divide-border">
              {activeRads.map((r) => {
                const checked = selectedSet.has(r.id);
                return (
                  <li key={r.id}>
                    <label
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-accent/50"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleRad(r.id)}
                      />
                      <span className="truncate">{r.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Presets pick everyone whose shifts fall in that window over the last/next ~30 days.
        </p>
      </div>

      {subscriptionQuery.isLoading ? (
        <FeedLoading />
      ) : subscriptionQuery.isError || !subscriptionQuery.data ? (
        <FeedError message={subscriptionQuery.error?.message} />
      ) : selectedIds.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Pick at least one radiologist to generate your calendar link.
        </p>
      ) : (
        <FeedBody
          urls={subscriptionQuery.data}
          canRegenerate
          onRegenerate={() => regenerate.mutate()}
          regenerating={regenerate.isPending}
        />
      )}
    </div>
  );
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function classifyRadsByBucket(
  rads: { id: number }[],
  availability: Record<number, Record<string, number[]>>,
): Record<Exclude<ShiftPresetKey, "all">, number[]> {
  // A rad belongs to a bucket if ANY of their active halfhours over the
  // sampled window falls in that bucket. Inclusive on purpose — radiologists
  // who occasionally cover nights show up under "Night" even if days are
  // their primary shift.
  const buckets: Record<Exclude<ShiftPresetKey, "all">, number[]> = {
    daytime: [],
    mid: [],
    night: [],
  };
  for (const rad of rads) {
    const days = availability[rad.id];
    if (!days) continue;
    let hasDay = false;
    let hasMid = false;
    let hasNight = false;
    for (const halfhours of Object.values(days)) {
      for (const hh of halfhours) {
        if (hh >= 16 && hh < 34) hasDay = true;
        else if (hh >= 34) hasMid = true;
        else hasNight = true;
        if (hasDay && hasMid && hasNight) break;
      }
      if (hasDay && hasMid && hasNight) break;
    }
    if (hasDay) buckets.daytime.push(rad.id);
    if (hasMid) buckets.mid.push(rad.id);
    if (hasNight) buckets.night.push(rad.id);
  }
  return buckets;
}

// --- Shared body (deep links, QR, controls) -------------------------------

type FeedUrls = {
  https_url: string;
  webcal_url: string;
  google_add_url: string;
  outlook_add_url: string;
};

type FeedBodyProps = {
  urls: FeedUrls;
  canRegenerate: boolean;
  onRegenerate: () => void;
  regenerating: boolean;
};

function FeedLoading() {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
      <Spinner /> Loading calendar link…
    </div>
  );
}

function FeedError({ message }: { message?: string }) {
  return (
    <div className="text-sm text-destructive py-4">
      Couldn&apos;t load the calendar link. {message ?? ""}
    </div>
  );
}

function FeedBody({ urls, canRegenerate, onRegenerate, regenerating }: FeedBodyProps) {
  const platform = useMemo(() => detectPlatform(), []);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [showUrl, setShowUrl] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current != null) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!urls.webcal_url) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(urls.webcal_url, {
      width: 192,
      margin: 1,
      color: { dark: "#0f172a", light: "#ffffff" },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [urls.webcal_url]);

  const onCopy = async () => {
    if (!urls.https_url) return;
    try {
      await navigator.clipboard.writeText(urls.https_url);
      if (copyTimeoutRef.current != null) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => {
        copyTimeoutRef.current = null;
        setCopied(false);
      }, 1500);
    } catch {
      toast.error("Couldn't copy. Long-press the URL to copy manually.");
    }
  };

  const buttons = orderForPlatform(platform, urls);

  return (
    <div className="space-y-5">
      <div className="grid gap-2 sm:grid-cols-2">
        {buttons.map((b) => (
          <Button
            key={b.label}
            variant={b.primary ? "default" : "outline"}
            onClick={() => window.open(b.href, "_blank", "noopener,noreferrer")}
            className="justify-start"
          >
            {b.icon}
            <span>{b.label}</span>
          </Button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          or scan with your phone
        </span>
        <Separator className="flex-1" />
      </div>

      <div className="flex flex-col items-center gap-3">
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt="QR code to subscribe to this shift calendar"
            className="rounded-md ring-1 ring-border"
            width={192}
            height={192}
          />
        ) : (
          <div className="h-[192px] w-[192px] rounded-md bg-muted" />
        )}
        <p className="text-xs text-muted-foreground text-center max-w-sm">
          Open your phone&apos;s camera, point at the code, then tap the prompt to add the calendar.
        </p>
      </div>

      <Separator />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowUrl((v) => !v)}>
            {showUrl ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showUrl ? "Hide URL" : "Show URL"}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCopy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy URL"}
          </Button>
          {canRegenerate ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmRotate(true)}
              disabled={regenerating}
            >
              <RefreshCw className="h-4 w-4" />
              Regenerate URL
            </Button>
          ) : null}
        </div>
        {showUrl ? (
          <code className="block break-all rounded-md bg-muted px-3 py-2 text-xs">
            {urls.https_url}
          </code>
        ) : null}
      </div>

      {platform === "android" ? (
        <p className="text-xs text-muted-foreground">
          <strong>Android note:</strong> Google Calendar&apos;s mobile app can&apos;t add subscribed
          calendars directly. Open the Google Calendar link on a computer once — after that, the
          shifts will sync to the Google Calendar app on your phone automatically.
        </p>
      ) : null}

      <p className="text-xs text-muted-foreground">
        Anyone with this URL can see this shift schedule (read-only). Share with a scribe or
        coworker — they paste the URL into their calendar app. Regenerate to revoke access.
      </p>

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate calendar URL?</AlertDialogTitle>
            <AlertDialogDescription>
              Any device or person currently subscribed with the old URL will stop receiving
              updates. They&apos;ll need to re-add the new URL.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onRegenerate();
                setConfirmRotate(false);
              }}
            >
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type LinkButton = {
  label: string;
  href: string;
  icon: React.ReactNode;
  primary?: boolean;
};

function orderForPlatform(
  platform: ReturnType<typeof detectPlatform>,
  urls: FeedUrls,
): LinkButton[] {
  const apple: LinkButton = {
    label: "Add to Apple Calendar",
    href: urls.webcal_url,
    icon: <Apple className="h-4 w-4" />,
  };
  const google: LinkButton = {
    label: "Add to Google Calendar",
    href: urls.google_add_url,
    icon: <CalendarIcon className="h-4 w-4" />,
  };
  const outlook: LinkButton = {
    label: "Add to Outlook",
    href: urls.outlook_add_url,
    icon: <Mail className="h-4 w-4" />,
  };

  switch (platform) {
    case "ios":
    case "mac":
      return [{ ...apple, primary: true }, google, outlook];
    case "android":
      return [{ ...google, primary: true }, apple, outlook];
    case "windows":
      return [{ ...outlook, primary: true }, google, apple];
    default:
      return [{ ...google, primary: true }, apple, outlook];
  }
}
